import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import { AnimatePresence, cubicBezier, motion, wrap } from 'framer-motion';
import type { Transition } from 'framer-motion';

import { cn, Image, Tooltip, useDisclosure } from 'src/shared/components/ui';
import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Typography
} from 'src/shared/components/ui';

const sliderVariants = {
  incoming: (direction: any) => ({
    x: direction > 0 ? '100%' : '-100%',
    scale: 1.2,
    opacity: 0
  }),
  active: { x: 0, scale: 1, opacity: 1 },
  exit: (direction: any) => ({
    x: direction > 0 ? '-100%' : '100%',
    scale: 1,
    opacity: 0.2
  })
};

const sliderTransition: Transition = {
  duration: 1,
  ease: cubicBezier(0.56, 0.03, 0.12, 1.04)
};

const ThumbnailImage = ({
  imageUrl,
  index,
  isCarousel,
  activeImageIndex,
  onOpenChange,
  skipToImage,
  maxImages,
  isLarge,
  isXL,
  imagesLeft = 0
}: {
  imageUrl: any;
  index: number;
  isCarousel?: boolean;
  activeImageIndex: number;
  onOpenChange: () => void;
  skipToImage: (index: number) => void;
  maxImages: number;
  isLarge?: boolean;
  isXL?: boolean;
  imagesLeft: number;
}) => {
  return (
    <div
      onClick={() => {
        skipToImage(index);

        if (!isCarousel || index === maxImages - 1) {
          if (imagesLeft > 0) {
            onOpenChange();
          }
        }

        if (!isCarousel) {
          onOpenChange();
        }
      }}
      key={index}
      className="relative"
    >
      <Image
        draggable="false"
        className="!w-full !h-full cursor-pointer overflow-hidden"
        classNames={{
          wrapper: cn(
            '!w-full !h-full object-cover !border border-gray-4 hover:opacity-60 transition-opacity !size-12 min-w-12 min-h-12',
            isLarge && '!min-w-[140px] !min-h-[140px]',
            isXL && '!min-w-[320px] !min-h-[320px] !max-w-full !rounded-2xl',
            isCarousel && '!min-w-14 !min-h-14 !border-2 !border-transparent',
            isCarousel &&
              index === activeImageIndex &&
              '!border-2 !border-primary'
          ),
          img: cn('object-cover rounded-xl', isXL && '!rounded-2xl')
        }}
        src={imageUrl?.url}
        alt={imageUrl?.description || imageUrl?.url}
      />
      {activeImageIndex === index && isCarousel && (
        <motion.div
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 1.2, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'linear' }}
          className="absolute inset-[2px] flex items-center justify-center rounded-xl z-20 cursor-pointer border-4 border-white"
        />
      )}
      {index === maxImages - 1 && imagesLeft > 0 && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl z-20 cursor-pointer">
          <Typography variant="body-1" weight="medium" className="text-white">
            {imagesLeft}+
          </Typography>
        </div>
      )}
    </div>
  );
};

interface ImageGalleryProps {
  imageUrls: {
    url: string;
    description?: string | null;
    name?: string | null;
  }[];
  title?: string;
  isLarge?: boolean;
  isXL?: boolean;
  isCarousel?: boolean;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  imageUrls,
  title = 'Images',
  isLarge,
  isXL,
  isCarousel
}) => {
  const { isOpen, onOpenChange } = useDisclosure();
  const [[imageCount, direction], setImageCount] = useState([0, 0]);
  const activeImageIndex = wrap(0, imageUrls.length, imageCount);

  const swipeToImage = (swipeDirection: any) => {
    setImageCount([imageCount + swipeDirection, swipeDirection]);
  };

  const dragEndHandler = (dragInfo: any) => {
    const draggedDistance = dragInfo.offset.x;
    const swipeThreshold = 50;

    if (draggedDistance > swipeThreshold) {
      swipeToImage(-1);
    } else if (draggedDistance < -swipeThreshold) {
      swipeToImage(1);
    }
  };

  const skipToImage = (imageId: any) => {
    let changeDirection: any;

    if (imageId > activeImageIndex) {
      changeDirection = 1;
    } else if (imageId < activeImageIndex) {
      changeDirection = -1;
    }
    setImageCount([imageId, changeDirection]);
  };

  const nextImage = () => {
    skipToImage(activeImageIndex + 1);
  };

  const prevImage = () => {
    skipToImage(activeImageIndex - 1);
  };

  const renderPreviewList = () => {
    const maxImages = 8;
    const images = [...imageUrls].slice(0, maxImages);
    const imagesLeft = imageUrls.length - maxImages;

    return (
      <div className="flex items-center flex-wrap gap-[4px]">
        {images.map((imageUrl, index) =>
          imageUrl?.description ? (
            <Tooltip closeDelay={0} key={index} content={imageUrl?.description}>
              <div>
                <ThumbnailImage
                  imageUrl={imageUrl}
                  index={index}
                  isCarousel={isCarousel}
                  activeImageIndex={activeImageIndex}
                  onOpenChange={onOpenChange}
                  skipToImage={skipToImage}
                  maxImages={maxImages}
                  isLarge={isLarge}
                  isXL={isXL}
                  imagesLeft={imagesLeft}
                />
              </div>
            </Tooltip>
          ) : (
            <ThumbnailImage
              key={index}
              imageUrl={imageUrl}
              index={index}
              isCarousel={isCarousel}
              activeImageIndex={activeImageIndex}
              onOpenChange={onOpenChange}
              skipToImage={skipToImage}
              maxImages={maxImages}
              isLarge={isLarge}
              isXL={isXL}
              imagesLeft={imagesLeft}
            />
          )
        )}
      </div>
    );
  };

  return (
    <>
      {isCarousel && (
        <div className="h-[464px] w-full relative rounded-xl mb-2 overflow-hidden">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              onClick={() => {
                onOpenChange();
              }}
              key={imageCount}
              custom={direction}
              variants={sliderVariants}
              initial="incoming"
              animate="active"
              exit="exit"
              transition={sliderTransition}
              drag={imageUrls.length > 1 ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_, dragInfo) => dragEndHandler(dragInfo)}
              className="absolute inset-0 w-full h-full justify-center items-center flex cursor-pointer overflow-hidden rounded-2xl"
              style={{
                backgroundImage: `url(${imageUrls[activeImageIndex]?.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-white/30 backdrop-blur-[100px] z-10 rounded-2xl" />

              <Image
                radius="none"
                draggable="false"
                className="!w-full !h-full"
                classNames={{
                  wrapper: '!w-full !h-full object-contain',
                  img: '!w-full !h-full object-contain',
                  blurredImg: '!w-full !h-full object-contain',
                  zoomedWrapper: '!w-full !h-full object-contain'
                }}
                src={imageUrls[activeImageIndex]?.url}
                alt={
                  imageUrls[activeImageIndex]?.name ||
                  imageUrls[activeImageIndex]?.url
                }
              />
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      {renderPreviewList()}
      <Modal size="full" isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
                {title}
              </ModalHeader>
              <ModalBody className="m-5 mb-10 flex flex-col justify-center items-center overflow-hidden relative">
                {imageUrls.length > 1 && (
                  <div className="absolute z-[999] left-0 top-1/2 -translate-y-1/2">
                    <ButtonV2 color="secondary" isIconOnly onClick={prevImage}>
                      <ArrowLeftIcon className="size-4 pointer-events-none" />
                    </ButtonV2>
                  </div>
                )}
                <AnimatePresence initial={false} custom={direction}>
                  <motion.div
                    key={imageCount}
                    custom={direction}
                    variants={sliderVariants}
                    initial="incoming"
                    animate="active"
                    exit="exit"
                    transition={sliderTransition}
                    drag={imageUrls.length > 1 ? 'x' : false}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={1}
                    onDragEnd={(_, dragInfo) => dragEndHandler(dragInfo)}
                    className="absolute inset-0 w-full h-full justify-center items-center flex"
                  >
                    <Image
                      draggable="false"
                      className="!w-full !h-full"
                      classNames={{
                        wrapper: '!w-full !h-full object-contain',
                        img: '!w-full !h-full object-contain',
                        blurredImg: '!w-full !h-full object-contain',
                        zoomedWrapper: '!w-full !h-full object-contain'
                      }}
                      src={imageUrls[activeImageIndex]?.url}
                      alt={
                        imageUrls[activeImageIndex]?.name ||
                        imageUrls[activeImageIndex]?.url
                      }
                    />
                  </motion.div>
                </AnimatePresence>
                {imageUrls.length > 1 && (
                  <div className="absolute z-[999] top-1/2 right-0 -translate-y-1/2">
                    <ButtonV2 color="secondary" isIconOnly onClick={nextImage}>
                      <ArrowRightIcon className="size-4 pointer-events-none" />
                    </ButtonV2>
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="flex flex-col relative justify-center border-t border-gray-4 p-5">
                <div className="absolute -top-8 left-0 right-0 z-[10] flex items-center justify-center gap-1">
                  {imageUrls[activeImageIndex]?.description && (
                    <Typography variant="body-1" weight="bold">
                      {imageUrls[activeImageIndex]?.description}
                    </Typography>
                  )}
                </div>
                <div className="flex gap-2 justify-center items-center">
                  {imageUrls.map((imageUrl, index) => (
                    <Image
                      draggable="false"
                      onClick={() => {
                        skipToImage(index);
                      }}
                      key={index}
                      className="!w-full !h-full cursor-pointer"
                      classNames={{
                        wrapper: '!w-full !h-full object-cover',
                        img: '!w-full !h-full object-cover',
                        blurredImg: '!w-full !h-full object-cover',
                        zoomedWrapper: [
                          '!w-full !h-full object-cover !size-12 !min-w-12 !min-h-12 rounded-xl',
                          index === activeImageIndex && 'opacity-50'
                        ]
                      }}
                      isZoomed
                      src={imageUrl?.url}
                      alt={imageUrl?.description || imageUrl?.url}
                    />
                  ))}
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ImageGallery;
